import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { HomePage, HomeSkeleton } from '@/pages/home';
import { parseHomeTab, type HomeTab } from '@/pages/home/home-tabs';
import {
  LANDING_ROUTES,
  landingRedirectDone,
  markLandingRedirectDone,
  resolveMobileLanding,
  type LandingSource,
} from '@/pages/home/lib/landing';
import { useMe } from '@/shared/api';
import { useMobile } from '@/shared/lib/use-mobile';
import { useAuthStore } from '@/shared/stores/auth.store';

/**
 * `/` — Home (docs/release-1/17-V2-BUILD-PLAN.md §5 W4-C "Landing").
 *
 * Desktop always shows Home. On a phone the FIRST visit of `/` in a browser
 * tab follows my landing pref (`mobileDefaultTab`; `auto` → the server's
 * `mobileLandingTab`: My Work, or Home when I'm Lead+ in a regular team) and
 * one-shot-redirects there; after that, 🏠 in the bottom nav always shows
 * Home. While the decision is pending the page shows the Home skeleton and
 * does not fetch Home, so a My Work landing costs no Home request.
 */
function IndexRoute() {
  const isMobile = useMobile();
  const navigate = useNavigate();
  const meQuery = useMe();
  const snapshot = useAuthStore((s) => s.user);
  const { tab } = Route.useSearch();
  // An explicit Home tab link (`/?tab=tasks`) IS the landing: it is never bounced
  // to the phone landing tab — neither now nor when I tap Overview / 🏠 later.
  const [redirectedBefore] = useState(() => landingRedirectDone() || tab !== undefined);
  const source = (meQuery.data ?? snapshot) as LandingSource | null | undefined;
  const landing = isMobile && !redirectedBefore ? resolveMobileLanding(source) : 'dashboard';
  const target = landing && landing !== 'dashboard' ? LANDING_ROUTES[landing] : null;

  useEffect(() => {
    if (isMobile && tab !== undefined) markLandingRedirectDone();
  }, [isMobile, tab]);

  useEffect(() => {
    if (!target) return;
    markLandingRedirectDone();
    void navigate({ to: target, replace: true });
  }, [target, navigate]);

  // Unknown profile on a cold phone start: decide first (unless /api/me failed).
  const deciding = landing === null && !meQuery.isError;
  if (target || deciding) return <HomeSkeleton layout={isMobile ? 'mobile' : 'desktop'} />;
  return <HomePage tab={tab ?? 'overview'} />;
}

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): { tab?: HomeTab } => {
    const tab = parseHomeTab(search.tab);
    return tab ? { tab } : {};
  },
  component: IndexRoute,
});
